import React from 'react'
import { GetServerSidePropsContext } from 'next'
import { SearchProfilesPageProvider } from '../../../providers/SearchProfilesPage'
import { getSearchSpecialists } from '../../../api/specialist'
import { getSpecialtyBySlug } from '../../../api/specialties'
import {
  ProfileQueryTypes,
  ProfileTypes,
  SearchProfilesPageProviderFilters,
  SortType
} from '../../../providers/SearchProfilesPage/types'
import { getSearchPractices } from '../../../api/practice'
import findLocation, { compareLocationSlugs, extractLocationProps } from '../../../utils/findLocation'
import findPageRedirectMiddleware from '../../../utils/findPageRedirectMiddleware'
import getLanguageFromLocale from '../../../utils/getLanguageFromLocale'
import { PracticeProfile, SearchPageProps } from '../../../types'
import { SpecialistProfile } from '../../../types/specialist'
import transformSpecialists from '../../../utils/transformSpecialists'
import { transformPractices } from '../../../utils/transformPracticeProfile'
import { filteringProfileDataByPlan } from '../../../utils/filteringDataByPlan'
import { DEFAULT_SEARCH_DISTANCE } from '../../../constants/search'
import { getLocationsWithChildren } from '../../../api/locations'
import { generateBreadcrumb } from '../../../utils/generateBreadcrumb'
import {
  getLocationsAutocomplete,
  getLocationsAutocompleteSelfDatabase,
  getSearchKeywords
} from '../../../api/autocomplete'
import extractAllChildrenFromLocationsTree from '../../../utils/extractAllChildrenFromLocationsTree'
import { TENANTS } from '../../../constants/global'
import SearchContent from './SearchContent'
import returnNextErrorPage from '../../../utils/returnNextErrorPage'
import { getHermesSettings, Languages, SettingsStage } from '@hermes/settings'
import sortLanguages from '../../../utils/sortLanguages'

const stage = process.env.NEXT_PUBLIC_STAGE as SettingsStage

function SearchPageDefault({
  specialists,
  practices,
  insurers,
  relatedKeywords,
  typeOfSpecialist,
  languages,
  totalProfiles,
  defaultFilters,
  specialty,
  isMainKeywordConditionOrProcedure,
  breadcrumb,
  conditionsAndProcedures,
  specialties,
  locationsAutocomplete,
  currentLocation,
  defaultLocation,
  distance
}: Readonly<SearchPageProps>) {
  return (
    <SearchProfilesPageProvider
      specialists={specialists}
      practices={practices}
      distance={distance}
      defaultFilters={defaultFilters}
      typeOfSpecialist={typeOfSpecialist}
      relatedKeywords={relatedKeywords}
      isMainKeywordConditionOrProcedure={isMainKeywordConditionOrProcedure}
      specialty={specialty}
      totalProfiles={totalProfiles}
      defaultLocation={defaultLocation}
      breadcrumb={breadcrumb}
      conditionsAndProcedures={conditionsAndProcedures}
      specialties={specialties}
      currentLocation={currentLocation}
      locationsAutocomplete={locationsAutocomplete}
    >
      <SearchContent insurers={insurers} languages={languages} />
    </SearchProfilesPageProvider>
  )
}

// todo: fix complexity
// eslint-disable-next-line complexity
export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    findPageRedirectMiddleware(context)

    const searchPath = context.query?.search || []

    const urlKeywordSlug = searchPath[0]
    const location = searchPath[1]

    const locale = context.locale as string
    if (locale === TENANTS.AR_UAE) {
      return returnNextErrorPage({ statusCode: 404 })
    }
    const language = getLanguageFromLocale(locale)
    const isWPA = locale === TENANTS.WPA

    // eslint-disable-next-line import/no-dynamic-require, global-require
    const localesTranslations = require(`../../../localesTranslations/${language}/language.json`)

    const locationsResponsePromise = getLocationsWithChildren(locale)
    const languagesResponsePromise = getHermesSettings({
      type: 'languages',
      langTenantUrl: locale,
      stage
    }) as Promise<Languages>
    const insurersResponsePromise = getHermesSettings({
      type: 'insurers',
      langTenantUrl: locale,
      stage
    })

    const [locationsResponse, languagesResponse, insurersResponse] = await Promise.all([
      locationsResponsePromise,
      languagesResponsePromise,
      insurersResponsePromise
    ])

    const defaultLocations = extractAllChildrenFromLocationsTree(locationsResponse)
    const locations = locationsResponse

    const locationCoord = {
      lat: '51.4776721',
      lon: '0.1648559',
      locationName: '',
      country: false,
      distance: '25'
    }
    const extMode = location.includes('ext-')

    if (extMode) {
      const { lat = '', lon = '' } = extractLocationProps(location)
      locationCoord.lat = lat
      locationCoord.lon = lon
    } else {
      const foundLocation = findLocation(defaultLocations, location)
      if (!foundLocation) {
        return returnNextErrorPage({ statusCode: 404 })
      }
      locationCoord.lat = foundLocation.lat.toString()
      locationCoord.lon = foundLocation.lon.toString()
      locationCoord.locationName = foundLocation.name[language].toString()
      locationCoord.country = foundLocation.country
      locationCoord.distance = foundLocation.searchDistance?.toString() || 'nolimit'
    }

    const currentLocation = defaultLocations.find(({ slug, lat, lon }) => {
      if (extMode) {
        return locationCoord.lat === lat && locationCoord.lon === lon
      }
      return compareLocationSlugs(location, slug)
    })

    if (extMode && currentLocation) {
      currentLocation.searchDistance = Number(DEFAULT_SEARCH_DISTANCE)
      currentLocation.slug = null
    }

    const specialtyResponse = await getSpecialtyBySlug({
      language,
      locale,
      slug: urlKeywordSlug,
      ...(currentLocation?.searchDistance
        ? {
            distance: currentLocation?.searchDistance.toString(),
            lat: currentLocation?.lat,
            lon: currentLocation?.lon
          }
        : {})
    })

    if (!specialtyResponse) {
      return returnNextErrorPage({ statusCode: 404 })
    }

    const typeOfProfile = searchPath[0]
    const profileType = searchPath[2] as ProfileTypes
    const pageFromQuery = searchPath[3]?.includes('page-') ? Number(searchPath[3].split('-')[1]) : 1
    const isSpecialistSearch = profileType === ProfileTypes.specialists
    const isPracticesSearch = profileType === ProfileTypes.practices
    if (!isPracticesSearch && !isSpecialistSearch) {
      return returnNextErrorPage({ statusCode: 404 })
    }

    const translatedLanguages = sortLanguages(languagesResponse, language).map(({ name, isoCode, id }) => ({
      name: localesTranslations[isoCode] || name.charAt(0).toUpperCase() + name.slice(1),
      isoCode,
      id
    }))

    const specialties = [specialtyResponse.id.toString()].filter((s) => s !== '1')
    let profileResponse:
      | {
          data: SpecialistProfile[]
          total: number
          distance: string
        }
      | {
          data: PracticeProfile[]
          total: number
          distance: string
        }

    let defaultSearchDistance: string = locationCoord.country ? locationCoord.distance : DEFAULT_SEARCH_DISTANCE
    let defaultSortType: SortType = SortType.relevance

    const typeProfileBuilder = (type: string): ProfileQueryTypes | string => {
      const obj: { [key: string]: ProfileQueryTypes | string } = {
        // Note: This is a temporary solution, changes from the backend are needed
        // [ProfileQueryTypes.dentistry]: ProfileQueryTypes.dentistry,
        [ProfileQueryTypes.healthAndSocialCare]: ProfileQueryTypes.careHome
      }

      if (type in obj) {
        return obj[type]
      }

      return ''
    }

    const getProfileResponse = async (distance: string, sortType: SortType) => {
      const queryParams = {
        lang: locale,
        specialties,
        lat: locationCoord.lat,
        lon: locationCoord.lon,
        page: pageFromQuery,
        distance: (currentLocation?.searchDistance || (distance === 'nolimit' ? '' : distance)).toString(),
        sortType,
        premium: true,
        locationType: currentLocation?.type,
        type: typeProfileBuilder(typeOfProfile),
        slots: false
      }

      return isSpecialistSearch ? getSearchSpecialists(queryParams) : getSearchPractices(queryParams)
    }

    profileResponse = await getProfileResponse(defaultSearchDistance, defaultSortType)

    if (profileResponse?.total === 0) {
      defaultSearchDistance = '100'
      defaultSortType = SortType.nearest
      profileResponse = await getProfileResponse(defaultSearchDistance, defaultSortType)
    }

    const pageQuery: SearchProfilesPageProviderFilters = {
      locationId: '',
      profileType,
      searchString: '',
      specialistType: '',
      distance: defaultSearchDistance,
      appointmentType: '',
      gender: '',
      language: '',
      insurer: '',
      selectedKeyword: '',
      page: pageFromQuery || 1,
      location: locationCoord.locationName,
      lat: locationCoord.lat,
      lon: locationCoord.lon,
      specialties,
      sortType: defaultSortType,
      typeOfSpecialist: '',
      treatment: '',
      patientsChildren: false,
      feeAgreed: false,
      premium: true,
      urlKeyword: urlKeywordSlug
    }
    const filteredProfilesData = profileResponse.data.map((profile) =>
      filteringProfileDataByPlan({ [isSpecialistSearch ? 'specialist' : 'practice']: profile })
    )

    let breadcrumb = null
    let defaultSearchKeywords = null
    let locationsAutocompleteResponse = null
    let locationsAutocomplete = null

    if (currentLocation) {
      breadcrumb = !extMode
        ? generateBreadcrumb(
            locale,
            currentLocation.name[language],
            Number(locationCoord.lat),
            Number(locationCoord.lon),
            locations,
            language,
            context.req.url
          )
        : []

      defaultSearchKeywords = await getSearchKeywords({
        locale,
        keywordId: specialtyResponse.id,
        keywordType: specialtyResponse.type,
        locationId: currentLocation?.id
      })

      if (isWPA) {
        locationsAutocompleteResponse = await getLocationsAutocomplete({
          lang: locale,
          searchString: '',
          language
        })
      } else {
        locationsAutocompleteResponse = await getLocationsAutocompleteSelfDatabase({
          locationId: currentLocation?.id,
          lang: locale,
          keywordId: specialtyResponse?.id
        })
      }

      if (locationsAutocompleteResponse.statusCode === 500) {
        locationsAutocomplete = []
      } else {
        locationsAutocomplete = locationsAutocompleteResponse?.children || locationsAutocompleteResponse
      }
    }

    return {
      props: {
        ...defaultSearchKeywords,
        specialists: isSpecialistSearch ? transformSpecialists(filteredProfilesData as SpecialistProfile[]) : [],
        practices: isSpecialistSearch ? [] : transformPractices(filteredProfilesData as PracticeProfile[]),
        insurers: insurersResponse,
        typeOfSpecialist: specialtyResponse.typeOfSpecialist || [],
        relatedKeywords: specialtyResponse.relatedKeywords || [],
        languages: translatedLanguages,
        totalProfiles: profileResponse.total,
        specialty: specialtyResponse || null,
        defaultFilters: pageQuery,
        isMainKeywordConditionOrProcedure:
          specialtyResponse.type === 'condition' || specialtyResponse.type === 'procedure',
        breadcrumb,
        locationsAutocomplete,
        currentLocation: currentLocation || null,
        distance: profileResponse?.distance || null
      }
    }
  } catch (e) {
    console.error('e', e)
    context.res.statusCode = 500
    return returnNextErrorPage({ statusCode: 500 })
  }
}

export default SearchPageDefault
